import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash } from '@phosphor-icons/react';
import { Button } from 'components/ui/button';
import { FilterBar } from 'shared/components/filterBar';
import type { BulkAction, FilterSection } from 'shared/components/filterBar/types';
import { TablePagination } from 'components/TablePagination';
import { UniversalTable, TABLE_DESIGN } from 'shared/components/UniversalTable';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { useActiveUsers } from 'shared/contexts/users.context';
import { toast } from 'react-toastify';
import { useRenoTickets } from './hooks/useRenoTickets';
import { useRenoTicketGroups } from './hooks/useRenoTicketGroups';
import { RenoProjectGroupedTable } from './components/RenoProjectGroupedTable';
import { RenoBulkActionModals } from './components/RenoBulkActionModals';
import { ReopenReasonModal } from './components/ReopenReasonModal';
import { RenoViewSwitch } from './components/RenoViewSwitch';
import { useRenoListViewMode } from './hooks/useRenoListViewMode';
import { AddRenoTicketModal, RenoMailIntent } from './components/AddRenoTicketModal';
import { RenoMassMailModal } from './components/RenoMassMailModal';
import { sendRenoTicketEmail } from './utils/sendRenoEmail';
import { SendResultsModal } from 'screens/mass-mail';
import { buildRenoSendReport, RenoSendReport } from './utils/buildRenoSendResults';
import { createRenoCells } from './shared/renoCellFactory';
import { RENO_TABLE_CONFIG, createRenoPickerConfig } from './configs/renoTableConfig';
import { useRenoTags } from './hooks/useRenoTags';
import { useTenantBalances } from 'shared/components/tenant';
import { buildRenoFilterSections, RenoFilterOptions } from './configs/renoFilterSections';
import {
  RenoTicket,
  RenoTicketStatus,
  RenoTicketPriority,
} from './types/renoTicket';

export default function RenoHelpdeskListPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const { users: activeUsers } = useActiveUsers();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [isBulkStatusOpen, setIsBulkStatusOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  /** Ticket waarvoor de heropen-redenmodal open staat; null = gesloten. */
  const [reopenTicketId, setReopenTicketId] = useState<number | null>(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [filterOptions, setFilterOptions] = useState<RenoFilterOptions>({ projects: [], suppliers: [], buildings: [], units: [], messageTypes: [], assignees: [] });
  // Mailcomposer na "Aanmaken & mail versturen": net aangemaakt ticket + kanaal + ontvangers.
  const [composeState, setComposeState] = useState<{ ticket: RenoTicket; channel: 'supplier' | 'tenant'; recipients: { name: string; email: string }[] } | null>(null);
  const [sendReport, setSendReport] = useState<RenoSendReport | null>(null);

  // "recent" = vlakke lijst met de nieuwste tickets, "project" = gegroepeerd per
  // renovatieproject en daarbinnen per onderdeel.
  const { viewMode, setViewMode } = useRenoListViewMode();
  const isProjectView = viewMode === 'project';

  const {
    tickets,
    total,
    openCount,
    completionCount,
    page,
    setPage,
    pageSize,
    isLoading,
    filters,
    setFilters,
    selectedStatuses,
    setSelectedStatuses,
    fetchTickets,
    createTicket,
  } = useRenoTickets();

  // Enkel actief in de per-project weergave; filters worden gedeeld met de vlakke lijst.
  const {
    rows: groupedRows,
    groupsByKey,
    partsByKey,
    total: groupedTotal,
    openCount: groupedOpenCount,
    completionCount: groupedCompletionCount,
    loaded: groupedLoaded,
    truncated: groupedTruncated,
    isLoading: isGroupedLoading,
    refetch: refetchGroups,
  } = useRenoTicketGroups({
    filters,
    statuses: selectedStatuses,
    enabled: isProjectView,
  });

  // Statistieken en laadstatus volgen de actieve weergave.
  const displayedTotal = isProjectView ? groupedTotal : total;
  const displayedOpenCount = isProjectView ? groupedOpenCount : openCount;
  const displayedCompletionCount = isProjectView ? groupedCompletionCount : completionCount;

  /** Herlaadt de actieve weergave (de andere hook doet niets zolang ze uit staat). */
  const refreshTickets = useCallback(async () => {
    await Promise.all([fetchTickets(), refetchGroups()]);
  }, [fetchTickets, refetchGroups]);

  const selectedIds = useMemo(() => Array.from(selectedRows), [selectedRows]);

  // Openstand van de contactpersonen op deze pagina — één call voor alle rijen samen,
  // niet één per rij. Beide weergaven gebruiken dezelfde rijen, dus dezelfde bron.
  const reporterIds = useMemo(
    () => (isProjectView ? groupedRows : tickets).map((ticket: any) => ticket?.reporter?.id),
    [isProjectView, groupedRows, tickets],
  );
  const { balances: tenantBalances } = useTenantBalances(reporterIds);

  // Tagcatalogus voor de Categorie-kolom: dezelfde lijst als op de detailpagina, zodat
  // je vanuit de lijst geen categorie buiten die lijst kan zetten.
  const { tags: tagCatalog } = useRenoTags();
  const pickerConfig = useMemo(
    () => createRenoPickerConfig(tagCatalog.map((tag) => tag.name)),
    [tagCatalog],
  );

  // Ticket aanmaken; bij mailIntent direct de mailcomposer openen voor het nieuwe ticket.
  const handleCreate = useCallback(async (payload: any, mailIntent?: RenoMailIntent) => {
    const created = await createTicket(payload);
    if (created) await refetchGroups();
    if (created && mailIntent && mailIntent.recipients.length > 0) {
      setComposeState({ ticket: created as RenoTicket, channel: mailIntent.channel, recipients: mailIntent.recipients });
    }
    return created;
  }, [createTicket, refetchGroups]);

  const handleComposeSend = useCallback(async (emailData: any) => {
    if (!composeState) return;
    // Type van de mail: hier is er geen aparte keuzelijst (de composer opent meteen na het
    // aanmaken), dus valt ze terug op het type dat bij het aanmaken van het ticket gekozen is.
    const result = await sendRenoTicketEmail(
      composeState.ticket.id,
      emailData,
      composeState.channel,
      session,
      composeState.ticket.messageType ?? null,
    );

    // Composer sluit meteen; dit rapport toont naar wie er gemaild is en of het lukte.
    setSendReport(buildRenoSendReport(result, emailData.contacts ?? [], emailData.subject ?? ''));

    await refreshTickets();
    return result;
  }, [composeState, session, refreshTickets]);

  useEffect(() => {
    if (!session) return;
    fetchWithAuth(`${API_URL}/reno-helpdesk/filter-options`, { method: 'GET' }, session)
      .then((r) => r.json())
      .then((data) => setFilterOptions(data))
      .catch(() => {});
  }, [session]);

  useEffect(() => { setSelectedRows(new Set()); }, [page, filters, viewMode]);

  // Debounced refetch nadat een inline picker een ticket heeft bijgewerkt.
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleTableUpdate = useCallback(() => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => refreshTickets(), 250);
  }, [refreshTickets]);

  const handleFilterUpdate = useCallback((key: string, values: string[]) => {
    setActiveFilters((prev) => ({ ...prev, [key]: values }));
    setPage(1);
    if (key === 'status') {
      setSelectedStatuses(values as RenoTicketStatus[]);
    } else if (key === 'priority') {
      setFilters((prev) => ({ ...prev, priorities: values.length ? values : undefined }));
    } else if (key === 'project') {
      setFilters((prev) => ({ ...prev, renovationProjectId: values[0] ? Number(values[0]) : undefined }));
    } else if (key === 'supplier') {
      setFilters((prev) => ({ ...prev, supplierId: values[0] ? Number(values[0]) : undefined }));
    } else if (key === 'building') {
      setFilters((prev) => ({ ...prev, buildingId: values[0] || undefined }));
    } else if (key === 'messageType') {
      setFilters((prev) => ({ ...prev, messageTypes: values.length ? values : undefined }));
    } else if (key === 'assignee') {
      setFilters((prev) => ({ ...prev, assignedTo: values[0] || undefined }));
    } else if (key === 'emailStatus') {
      // Enkel `true` of weg: er is geen filter "tickets zonder ongelezen mail".
      setFilters((prev) => ({
        ...prev,
        hasUnreadEmails: values.includes('unread') ? true : undefined,
      }));
    }
  }, [setSelectedStatuses, setFilters, setPage]);

  const filterSections = useMemo<FilterSection[]>(
    () =>
      buildRenoFilterSections({
        activeFilters,
        filterOptions,
        activeUsers,
        onUpdate: handleFilterUpdate,
      }),
    [activeFilters, filterOptions, activeUsers, handleFilterUpdate],
  );

  const updatePriority = useCallback(async (ticketId: number, priority: RenoTicketPriority) => {
    if (!session) return;
    await fetchWithAuth(
      `${API_URL}/reno-helpdesk/tickets/${ticketId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ priority }) },
      session,
    );
    handleTableUpdate();
  }, [session, handleTableUpdate]);

  const completeTicket = useCallback(async (ticketId: number) => {
    if (!session) return;
    await fetchWithAuth(
      `${API_URL}/reno-helpdesk/tickets/${ticketId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: RenoTicketStatus.AFGEROND }) },
      session,
    );
    toast.success('Ticket afgerond');
    refreshTickets();
  }, [session, refreshTickets]);

  /**
   * Heropenen vraagt een reden (die wordt als actienota bewaard), dus de gele pijl in de
   * lijst opent deze modal in plaats van meteen de fase te wijzigen.
   */
  const reopenTicket = useCallback(async (reason: string) => {
    if (!session || reopenTicketId === null) return;
    const res = await fetchWithAuth(
      `${API_URL}/reno-helpdesk/tickets/${reopenTicketId}/reopen`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) },
      session,
    );
    if (!res.ok) {
      toast.error('Heropenen mislukt');
      return;
    }
    toast.success('Ticket heropend');
    setReopenTicketId(null);
    refreshTickets();
  }, [session, reopenTicketId, refreshTickets]);

  const deleteTicketById = useCallback(async (ticketId: number) => {
    if (!session) return;
    await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/${ticketId}`, { method: 'DELETE' }, session);
    toast.success('Ticket verwijderd');
    refreshTickets();
  }, [session, refreshTickets]);

  // Priority-sterren (zelfde rendering als helpdesk); gebonden aan het ticketId.
  const renderPriorityFor = useCallback((ticketId: number, priority: any) => {
    const filled = priority === RenoTicketPriority.HIGH ? 3 : priority === RenoTicketPriority.MEDIUM ? 2 : priority === RenoTicketPriority.LOW ? 1 : 0;
    const onStar = async (e: React.MouseEvent, idx: number) => {
      e.stopPropagation();
      const n = idx + 1;
      const np = n === 3 ? RenoTicketPriority.HIGH : n === 2 ? RenoTicketPriority.MEDIUM : RenoTicketPriority.LOW;
      await updatePriority(ticketId, np);
    };
    return (
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((index) => {
          const isActive = index < filled;
          return (
            <span
              key={index}
              className={`${TABLE_DESIGN.cellContent.priority.star} ${isActive ? TABLE_DESIGN.cellContent.priority.active : TABLE_DESIGN.cellContent.priority.inactive} ${TABLE_DESIGN.cellContent.priority.interactive}`}
              onClick={(e) => onStar(e, index)}
            >
              {isActive ? '★' : '☆'}
            </span>
          );
        })}
      </div>
    );
  }, [updatePriority]);

  // Beide weergaven gebruiken dezelfde rij-opbouw; de per-project weergave krijgt
  // er een Project- en Onderdeel-kolom bij (withProjectColumns).
  const buildRow = useCallback((ticket: any, context: any, withProjectColumns: boolean) => {
    return createRenoCells({
      ticket,
      context,
      handlers: {
        onComplete: (e: React.MouseEvent) => { e.stopPropagation(); completeTicket(ticket.id); },
        onReopen: () => setReopenTicketId(ticket.id),
        onMerge: () => navigate(`/renotickets/${ticket.id}`),
        onDelete: () => deleteTicketById(ticket.id),
        onTitleClick: (e: React.MouseEvent) => { e.stopPropagation(); navigate(`/renotickets/${ticket.id}`); },
      },
      renderPriority: (p: any) => renderPriorityFor(ticket.id, p),
      users: activeUsers,
      options: { withProjectColumns },
      tenantBalances,
    });
  }, [activeUsers, completeTicket, deleteTicketById, navigate, renderPriorityFor, tenantBalances]);

  const renderRow = useCallback(
    (ticket: any, context: any) => buildRow(ticket, context, false),
    [buildRow],
  );

  const renderProjectRow = useCallback(
    (ticket: any, context: any) => buildRow(ticket, context, true),
    [buildRow],
  );

  const bulkRequest = async (body: object) => {
    if (!session || selectedIds.length === 0) return;
    setIsBulkSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/bulk`,
        { method: 'PATCH', body: JSON.stringify({ ids: selectedIds, ...body }), headers: { 'Content-Type': 'application/json' } },
        session,
      );
      if (!res.ok) throw new Error('Bulk actie mislukt');
      toast.success(`${selectedIds.length} ticket(s) bijgewerkt`);
      setSelectedRows(new Set());
      await refreshTickets();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleBulkClose = () => bulkRequest({ status: RenoTicketStatus.AFGEROND });
  const handleBulkStatusChange = async (status: RenoTicketStatus) => bulkRequest({ status });
  const handleBulkAssign = async (user: any) => { setIsBulkAssignOpen(false); await bulkRequest({ assignedTo: user?.id ?? null }); };

  const handleBulkDelete = async () => {
    if (!session) return;
    setIsDeleteConfirmOpen(false);
    setIsBulkSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/bulk`,
        { method: 'DELETE', body: JSON.stringify({ ids: selectedIds }), headers: { 'Content-Type': 'application/json' } },
        session,
      );
      if (!res.ok) throw new Error('Verwijderen mislukt');
      const result = await res.json();
      toast.success(`${result.deletedIds?.length ?? selectedIds.length} ticket(s) verwijderd`);
      if (result.failedIds?.length) toast.warning(`${result.failedIds.length} ticket(s) konden niet worden verwijderd`);
      setSelectedRows(new Set());
      await refreshTickets();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleExport = async () => {
    if (!session) return;
    try {
      const params = new URLSearchParams();
      if (selectedIds.length > 0) {
        params.append('ids', selectedIds.join(','));
      } else {
        if (filters.search) params.append('search', filters.search);
        if (filters.renovationProjectId) params.append('renovationProjectId', String(filters.renovationProjectId));
        if (filters.supplierId) params.append('supplierId', String(filters.supplierId));
        if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
      }
      const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/export?${params}`, { method: 'GET' }, session);
      if (!res.ok) throw new Error('Export mislukt');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reno-tickets-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export mislukt');
    }
  };

  const bulkActions = useMemo<BulkAction[]>(() => [
    { label: `Exporteer naar Excel (${selectedIds.length})`, onClick: handleExport, variant: 'secondaryNeutral', disabled: selectedIds.length === 0 || isBulkSubmitting },
    { label: `Fase wijzigen (${selectedIds.length})`, onClick: () => setIsBulkStatusOpen(true), variant: 'primaryMoss', disabled: selectedIds.length === 0 || isBulkSubmitting },
    { label: `Toewijzen (${selectedIds.length})`, onClick: () => setIsBulkAssignOpen(true), variant: 'yellow', disabled: selectedIds.length === 0 || isBulkSubmitting },
    { label: `Sluiten (${selectedIds.length})`, onClick: handleBulkClose, variant: 'blue', disabled: selectedIds.length === 0 || isBulkSubmitting },
    { label: `Verwijderen (${selectedIds.length})`, icon: <Trash size={13} />, onClick: () => setIsDeleteConfirmOpen(true), variant: 'red', disabled: selectedIds.length === 0 || isBulkSubmitting },
  ], [selectedIds, isBulkSubmitting]);

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0">
        {/* Header */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Renovatietickets</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Paginatie enkel bij "Recent"; de per-project weergave laadt
                  volledige projecten in één keer zodat je ze kunt open- en
                  dichtklikken. */}
              {!isProjectView && (
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={total}
                  onPageChange={setPage}
                  isLoading={isLoading}
                />
              )}
              <RenoViewSwitch value={viewMode} onChange={setViewMode} />
              <Button onClick={() => setIsAddModalOpen(true)} size="sm">
                <span className="h-5 w-5 text-lg font-bold">+</span>
                Ticket toevoegen
              </Button>
            </div>
          </div>
        </header>

        {/* Statistieken */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-6">
            <div className="flex flex-col">
              <span className="text-neutral-primary text-2xl font-bold">{displayedOpenCount ?? '...'}</span>
              <span className="text-xs text-gray-500 uppercase">Open tickets</span>
            </div>
            <div className="h-10 w-px bg-gray-300" />
            <div className="flex flex-col">
              <span className="text-moss-primary text-2xl font-bold">{displayedCompletionCount ?? '...'}</span>
              <span className="text-xs text-gray-500 uppercase">Afgerond</span>
            </div>
            <div className="h-10 w-px bg-gray-300" />
            <div className="flex flex-col">
              <span className="text-moss-primary text-2xl font-bold">{displayedTotal ?? '...'}</span>
              <span className="text-xs text-gray-500 uppercase">Totaal (huidige filters)</span>
            </div>
          </div>
        </div>

        {isProjectView ? (
          <RenoProjectGroupedTable
            rows={groupedRows}
            groupsByKey={groupsByKey}
            partsByKey={partsByKey}
            isLoading={isGroupedLoading}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            renderRow={renderProjectRow}
            bulkActions={bulkActions}
            onUpdate={handleTableUpdate}
            truncated={groupedTruncated}
            loaded={groupedLoaded}
            total={groupedTotal}
            pickerConfig={pickerConfig}
          />
        ) : (
          <UniversalTable
            columnsConfig={RENO_TABLE_CONFIG.columnsConfig}
            pickerConfig={pickerConfig}
            sortConfig={RENO_TABLE_CONFIG.sortConfig}
            rows={tickets}
            groupBy="none"
            title=""
            isLoading={isLoading}
            renderRow={renderRow}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            searchConfig={{ bulkActions }}
            onUpdate={handleTableUpdate}
          />
        )}
      </div>

      {/* FilterBar op parent-niveau */}
      <FilterBar
        searchValue={filters.search ?? ''}
        onSearchChange={(v) => { setFilters((prev) => ({ ...prev, search: v })); setPage(1); }}
        searchPlaceholder="Zoek tickets op titel, project, gebouw, contactpersoon..."
        filterSections={filterSections}
        showWhenEmpty
        showSpacer={false}
      />

      <AddRenoTicketModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onCreated={handleCreate} />

      {composeState && (
        <RenoMassMailModal
          isOpen={true}
          ticket={composeState.ticket}
          channel={composeState.channel}
          recipients={composeState.recipients}
          onClose={() => setComposeState(null)}
          onSuccess={() => { setComposeState(null); refreshTickets(); }}
          onSend={handleComposeSend}
          emailHistory={[]}
        />
      )}

      {/* Verzendrapport: naar wie er gemaild is en of het gelukt is. */}
      <SendResultsModal
        isOpen={sendReport !== null}
        onClose={() => setSendReport(null)}
        results={sendReport}
      />

      {/* Heropenen van een afgerond ticket vraagt een reden; die komt als actienota
          op het ticket te staan. */}
      {reopenTicketId !== null && (
        <ReopenReasonModal
          isOpen
          ticketId={reopenTicketId}
          onClose={() => setReopenTicketId(null)}
          onConfirm={reopenTicket}
        />
      )}

      <RenoBulkActionModals
        selectedCount={selectedIds.length}
        isSubmitting={isBulkSubmitting}
        isAssignOpen={isBulkAssignOpen}
        onAssignOpenChange={setIsBulkAssignOpen}
        onAssign={handleBulkAssign}
        isStatusOpen={isBulkStatusOpen}
        onStatusOpenChange={setIsBulkStatusOpen}
        onStatusChange={handleBulkStatusChange}
        isDeleteConfirmOpen={isDeleteConfirmOpen}
        onDeleteConfirmOpenChange={setIsDeleteConfirmOpen}
        onDelete={handleBulkDelete}
      />
    </>
  );
}
